import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import userService from '../../services/userService'

// Async thunks
export const fetchUsers = createAsyncThunk('users/fetchUsers', async ({ pageNumber = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
  try {
    const response = await userService.fetchUsers(
      pageNumber,
      pageSize,
      {
        showSnackbar: true,
        showSuccessSnackbar: false,
        successMessage: 'Users fetched successfully!',
        errorMessage: 'Failed to fetch users. Please try again.'
      }
    )
    return response
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const addUser = createAsyncThunk('users/addUser', async (userData, { rejectWithValue }) => {
  try {
    const response = await userService.addUser(
      userData,
      {
        showSnackbar: true,
        showSuccessSnackbar: true,
        successMessage: 'User added successfully!',
        errorMessage: 'Failed to add user. Please try again.'
      }
    )
    return response
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const updateUser = createAsyncThunk('users/updateUser', async ({ id, userData }, { rejectWithValue }) => {
  try {
    const response = await userService.updateUser(
      id,
      userData,
      {
        showSnackbar: true,
        showSuccessSnackbar: true,
        successMessage: 'User updated successfully!',
        errorMessage: 'Failed to update user. Please try again.'
      }
    )
    return response
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const uploadUsersCSV = createAsyncThunk('users/uploadUsersCSV', async (file, { rejectWithValue }) => {
  try {
    const response = await userService.uploadUsersCSV(
      file,
      {
        showSnackbar: true,
        showSuccessSnackbar: true,
        successMessage: 'File uploaded successfully, processing in progress, you will receive an email once it is completed.',
        errorMessage: 'Failed to upload users. Please check the file format and try again.',
        autoHideDuration: 8000
      }
    )
    return response
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const uploadFundsCSV = createAsyncThunk('users/uploadFundsCSV', async (file, { rejectWithValue }) => {
  try {
    const response = await userService.uploadFundsCSV(
      file,
      {
        showSnackbar: true,
        showSuccessSnackbar: true,
        successMessage: 'File uploaded successfully, processing in progress, you will receive an email once it is completed.',
        errorMessage: 'Failed to upload funds. Please check the file format and try again.',
        autoHideDuration: 8000
      }
    )
    return response
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const issueFunds = createAsyncThunk('users/issueFunds', async ({ participantIds, amount }, { rejectWithValue }) => {
  try {

    const payload = {
      amountperparticipant: amount,
      participants: participantIds.map(id => ({ participantid: id }))
    }

    const response = await userService.issueFunds(payload, {
      showSnackbar: true,
      showSuccessSnackbar: true,
      successMessage: 'Funds issued successfully!',
      errorMessage: 'Failed to issue funds. Please try again.'
    })
    return response
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const getParticipantsCSVTemplate = createAsyncThunk('users/getParticipantsCSVTemplate', async (_, { rejectWithValue }) => {
  try {
    const response = await userService.fetchParticipantsTemplate(
      {
        showSnackbar: true,
        showSuccessSnackbar: true,
        successMessage: 'Participants CSV template fetched successfully!',
        errorMessage: 'Failed to fetch participants CSV template'
      }
    )
    return response
  } catch (error) {
    return rejectWithValue(error.message)
  }
})


const initialState = {
  users: [],
  totalUsers: 0,
  loading: {
    fetch: false,
    add: false,
    update: false,
    uploadUsers: false,
    uploadFunds: false,
    issueFunds: false,
    csvTemplate: false,
  },
  error: {
    fetch: null,
    add: null,
    update: null,
    uploadUsers: null,
    uploadFunds: null,
    issueFunds: null,
    csvTemplate: null,
  },
}

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = {
        fetch: null,
        add: null,
        update: null,
        uploadUsers: null,
        uploadFunds: null,
        issueFunds: null,
        csvTemplate: null,
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading.fetch = true
        state.error.fetch = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading.fetch = false
        state.users = action.payload.participants || []
        state.totalUsers = action.payload.totalparticipantcount || 0

      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading.fetch = false
        state.error.fetch = action.payload
      })

    // Add user
    builder
      .addCase(addUser.pending, (state) => {
        state.loading.add = true
        state.error.add = null
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.loading.add = false
      })
      .addCase(addUser.rejected, (state, action) => {
        state.loading.add = false
        state.error.add = action.payload
      })

    // Update user
    builder
      .addCase(updateUser.pending, (state) => {
        state.loading.update = true
        state.error.update = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading.update = false
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading.update = false
        state.error.update = action.payload
      })

    // Upload users CSV
    builder
      .addCase(uploadUsersCSV.pending, (state) => {
        state.loading.uploadUsers = true
        state.error.uploadUsers = null
      })
      .addCase(uploadUsersCSV.fulfilled, (state) => {
        state.loading.uploadUsers = false
      })
      .addCase(uploadUsersCSV.rejected, (state, action) => {
        state.loading.uploadUsers = false
        state.error.uploadUsers = action.payload
      })

    // Upload funds CSV
    builder
      .addCase(uploadFundsCSV.pending, (state) => {
        state.loading.uploadFunds = true
        state.error.uploadFunds = null
      })
      .addCase(uploadFundsCSV.fulfilled, (state) => {
        state.loading.uploadFunds = false
      })
      .addCase(uploadFundsCSV.rejected, (state, action) => {
        state.loading.uploadFunds = false
        state.error.uploadFunds = action.payload
      })

    // Issue funds
    builder
      .addCase(issueFunds.pending, (state) => {
        state.loading.issueFunds = true
        state.error.issueFunds = null
      })
      .addCase(issueFunds.fulfilled, (state, action) => {
        state.loading.issueFunds = false
      })
      .addCase(issueFunds.rejected, (state, action) => {
        state.loading.issueFunds = false
        state.error.issueFunds = action.payload
      })
      // Get participants CSV template
      .addCase(getParticipantsCSVTemplate.pending, (state) => {
        state.loading.csvTemplate = true
        state.error.csvTemplate = null
      })
      .addCase(getParticipantsCSVTemplate.fulfilled, (state, action) => {
        state.loading.csvTemplate = false
      })
      .addCase(getParticipantsCSVTemplate.rejected, (state, action) => {
        state.loading.csvTemplate = false
        state.error.csvTemplate = action.payload
      })
  },
})

export const { clearErrors } = usersSlice.actions
export default usersSlice.reducer
